import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { buildResultGroups, isCodeQuery, normalizeCode, verificationKey } from './cartUtils';

const DEBOUNCE_MS = 350;
const MIN_LENGTH = 3;

const idle = { status: 'idle', items: [] };

/**
 * One search over every orderable source, fanned out in parallel: the part register,
 * the vendor catalogues and each API integration's live search (code-shaped queries
 * only; no vendor API searches by name). Each source carries its own status, so one
 * slow or failing vendor never blocks the others. Results populate as each returns.
 */
export function useOrderableSearch({ context, codeOnly }) {
  const [query, setQuery] = useState('');
  const [register, setRegister] = useState(idle);
  const [catalogue, setCatalogue] = useState({ ...idle, truncated: false });
  const [live, setLive] = useState({});
  const [verifications, setVerifications] = useState({});
  const seqRef = useRef(0);
  const debounceRef = useRef(null);

  const liveIntegrations = useMemo(
    () => (context?.integrations || []).filter((i) => i.channel === 'api' && i.supports_live_search),
    [context]
  );

  const run = useCallback(
    async (term) => {
      const q = term.trim();
      const seq = ++seqRef.current;
      if (q.length < MIN_LENGTH) {
        setRegister(idle);
        setCatalogue({ ...idle, truncated: false });
        setLive({});
        return;
      }
      setRegister({ status: 'loading', items: [] });
      setCatalogue({ status: 'loading', items: [], truncated: false });
      const codeLike = isCodeQuery(q);
      const liveInitial = {};
      liveIntegrations.forEach((integration) => {
        liveInitial[integration.id] = { status: codeLike ? 'loading' : 'skipped', hits: [] };
      });
      setLive(liveInitial);

      axios
        .get('/api/parts/', {
          params: {
            search: q,
            orderable: true,
            with_order_integration: true,
            typeahead: true,
            ...(codeOnly ? { code_only: true } : {}),
          },
        })
        .then((resp) => {
          if (seq !== seqRef.current) return;
          const rows = Array.isArray(resp.data) ? resp.data : resp.data?.results || [];
          setRegister({ status: 'ok', items: rows });
        })
        .catch(() => {
          if (seq === seqRef.current) setRegister({ status: 'failed', items: [] });
        });

      axios
        .get('/api/catalogue-search/', {
          params: { q, with_ordering: 1, ...(codeOnly ? { code_only: true } : {}) },
        })
        .then((resp) => {
          if (seq !== seqRef.current) return;
          setCatalogue({ status: 'ok', items: resp.data?.results || [], truncated: !!resp.data?.truncated });
        })
        .catch(() => {
          if (seq === seqRef.current) setCatalogue({ status: 'failed', items: [], truncated: false });
        });

      if (codeLike) {
        liveIntegrations.forEach((integration) => {
          axios
            .get(`/api/suppliers/${integration.id}/live-search/`, { params: { q } })
            .then((resp) => {
              if (seq !== seqRef.current) return;
              const hits = resp.data?.results || [];
              setLive((prev) => ({ ...prev, [integration.id]: { status: 'ok', hits } }));
              // Every live answer is also a verification for its code.
              setVerifications((prev) => {
                const next = { ...prev };
                hits.forEach((hit) => {
                  next[verificationKey(integration.id, hit.article_code)] = {
                    status: hit.recognised ? 'ok' : 'unknown',
                    hit,
                  };
                });
                return next;
              });
            })
            .catch(() => {
              if (seq === seqRef.current) setLive((prev) => ({ ...prev, [integration.id]: { status: 'failed', hits: [] } }));
            });
        });
      }
    },
    [codeOnly, liveIntegrations]
  );

  const search = useCallback(
    (term, { immediate = false } = {}) => {
      setQuery(term);
      window.clearTimeout(debounceRef.current);
      if (immediate) {
        run(term);
        return;
      }
      debounceRef.current = window.setTimeout(() => run(term), DEBOUNCE_MS);
    },
    [run]
  );

  useEffect(() => () => window.clearTimeout(debounceRef.current), []);

  // Re-run the current query when the code-only switch flips.
  const firstRef = useRef(true);
  useEffect(() => {
    if (firstRef.current) {
      firstRef.current = false;
      return;
    }
    if (query.trim().length >= MIN_LENGTH) run(query);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeOnly]);

  const retryLive = useCallback(
    (integrationId) => {
      const q = query.trim();
      if (!isCodeQuery(q)) return;
      const seq = seqRef.current;
      setLive((prev) => ({ ...prev, [integrationId]: { status: 'loading', hits: [] } }));
      axios
        .get(`/api/suppliers/${integrationId}/live-search/`, { params: { q } })
        .then((resp) => {
          if (seq !== seqRef.current) return;
          const hits = resp.data?.results || [];
          setLive((prev) => ({ ...prev, [integrationId]: { status: 'ok', hits } }));
          setVerifications((prev) => {
            const next = { ...prev };
            hits.forEach((hit) => {
              next[verificationKey(integrationId, hit.article_code)] = { status: hit.recognised ? 'ok' : 'unknown', hit };
            });
            return next;
          });
        })
        .catch(() => {
          if (seq === seqRef.current) setLive((prev) => ({ ...prev, [integrationId]: { status: 'failed', hits: [] } }));
        });
    },
    [query]
  );

  /**
   * Verify one code against one API integration (a catalogue row of an Örum/Atoy vendor
   * before it goes into the cart). Resolves to the live hit, or null when the vendor
   * does not recognise the code or did not answer.
   */
  const verifyCode = useCallback(async (integrationId, code) => {
    const key = verificationKey(integrationId, code);
    setVerifications((prev) => ({ ...prev, [key]: { status: 'checking', hit: null } }));
    try {
      const resp = await axios.get(`/api/suppliers/${integrationId}/live-search/`, { params: { q: code } });
      const wanted = normalizeCode(code);
      const hit = (resp.data?.results || []).find((h) => normalizeCode(h.article_code) === wanted && h.recognised) || null;
      setVerifications((prev) => ({ ...prev, [key]: { status: hit ? 'ok' : 'unknown', hit } }));
      return hit;
    } catch (err) {
      setVerifications((prev) => ({ ...prev, [key]: { status: 'failed', hit: null } }));
      return null;
    }
  }, []);

  const clearVerification = useCallback((integrationId, code) => {
    const key = verificationKey(integrationId, code);
    setVerifications((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const groups = useMemo(
    () =>
      buildResultGroups({
        register: register.items,
        catalogue: catalogue.items,
        live,
        verifications,
        context,
      }),
    [register.items, catalogue.items, live, verifications, context]
  );

  const active = query.trim().length >= MIN_LENGTH;
  const anyLoading =
    register.status === 'loading' ||
    catalogue.status === 'loading' ||
    Object.values(live).some((state) => state.status === 'loading');

  return {
    query,
    search,
    active,
    anyLoading,
    register,
    catalogue,
    live,
    liveIntegrations,
    groups,
    verifications,
    verifyCode,
    clearVerification,
    retryLive,
    codeLike: isCodeQuery(query),
  };
}

export default useOrderableSearch;
